/**
 * PerfilesCatalogo - CRUD de perfiles de examen
 */

import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Plus,
  MoreHorizontal,
  Pencil,
  CheckCircle,
  XCircle,
  Trash2,
  Search,
  X,
  RefreshCw,
  AlertCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import {
  DataTableColumnHeader,
  DataTablePagination,
  DataTableViewOptions,
} from "@/components/ui/data-table";
import { ResponsiveTable, useIsMobile } from "@/components/ui/responsive-table";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

import { useApiError } from "@/hooks/use-api-error";
import { useServerDataTable } from "@/hooks/use-server-data-table";
import {
  useDeactivatePerfil,
  useActivatePerfil,
  useDeletePerfil,
  type PerfilExamenListItem,
} from "@/hooks/use-ordenes";
import { getPerfiles } from "@/hooks/use-ordenes";

import { PerfilFormDialog } from "./perfil-form-dialog";

export function PerfilesCatalogo() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const isMobile = useIsMobile();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedPerfil, setSelectedPerfil] = useState<PerfilExamenListItem | null>(null);
  const [editPerfilId, setEditPerfilId] = useState<number | null>(null);
  const [confirmAction, setConfirmAction] = useState<"activate" | "deactivate" | "delete" | null>(
    null
  );

  const deactivateMutation = useDeactivatePerfil({
    onSuccess: () => {
      toast.warning(t("ordenes.perfiles.toast.deactivated", { name: selectedPerfil?.nombre }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const activateMutation = useActivatePerfil({
    onSuccess: () => {
      toast.success(t("ordenes.perfiles.toast.activated", { name: selectedPerfil?.nombre }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const deleteMutation = useDeletePerfil({
    onSuccess: () => {
      toast.error(t("ordenes.perfiles.toast.deleted", { name: selectedPerfil?.nombre }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const handleEdit = useCallback((perfil: PerfilExamenListItem) => {
    setEditPerfilId(perfil.id);
    setIsFormOpen(true);
  }, []);

  const handleToggleStatus = useCallback((perfil: PerfilExamenListItem) => {
    setSelectedPerfil(perfil);
    setConfirmAction(perfil.is_active ? "deactivate" : "activate");
    setIsConfirmOpen(true);
  }, []);

  const handleDelete = useCallback((perfil: PerfilExamenListItem) => {
    setSelectedPerfil(perfil);
    setConfirmAction("delete");
    setIsConfirmOpen(true);
  }, []);

  const handleConfirmAction = () => {
    if (!selectedPerfil || !confirmAction) return;
    if (confirmAction === "activate") activateMutation.mutate(selectedPerfil.id);
    else if (confirmAction === "deactivate") deactivateMutation.mutate(selectedPerfil.id);
    else if (confirmAction === "delete") deleteMutation.mutate(selectedPerfil.id);
    setIsConfirmOpen(false);
    setSelectedPerfil(null);
    setConfirmAction(null);
  };

  const handleFormSuccess = (isNew: boolean) => {
    setIsFormOpen(false);
    setEditPerfilId(null);
    toast.success(
      isNew ? t("ordenes.perfiles.toast.created") : t("ordenes.perfiles.toast.updated")
    );
    refetch();
  };

  const columns: ColumnDef<PerfilExamenListItem>[] = useMemo(
    () => [
      {
        accessorKey: "codigo",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.perfiles.columns.code")} />
        ),
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.codigo}</span>,
      },
      {
        accessorKey: "nombre",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.perfiles.columns.name")} />
        ),
        cell: ({ row }) => <span className="font-medium">{row.original.nombre}</span>,
      },
      {
        accessorKey: "num_examenes",
        header: t("ordenes.perfiles.columns.exams"),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {t("ordenes.perfiles.numExams", { count: row.original.num_examenes })}
          </span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "precio",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.perfiles.columns.price")} />
        ),
        cell: ({ row }) => (
          <span className="text-sm">L {Number(row.original.precio).toFixed(2)}</span>
        ),
      },
      {
        accessorKey: "is_active",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.perfiles.columns.status")} />
        ),
        cell: ({ row }) => (
          <Badge variant={row.original.is_active ? "default" : "secondary"}>
            {row.original.is_active ? t("common.active") : t("common.inactive")}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: () => t("common.actions"),
        cell: ({ row }) => {
          const perfil = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleEdit(perfil)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleToggleStatus(perfil)}>
                  {perfil.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!perfil.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(perfil)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        enableSorting: false,
      },
    ],
    [t, handleEdit, handleToggleStatus, handleDelete]
  );

  const {
    table,
    isLoading,
    error,
    totalCount,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  } = useServerDataTable({
    columns,
    fetchFn: getPerfiles,
    queryKey: "perfiles",
    storageKey: "perfiles_page_size",
    defaultPageSize: 20,
    filters: [{ key: "is_active", defaultValue: "all", urlParam: "perfil_status" }],
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-base sm:text-lg font-semibold">{t("ordenes.perfiles.title")}</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""} ${isMobile ? "" : "mr-2"}`}
            />
            {!isMobile && t("common.refresh")}
          </Button>
          <Button
            size={isMobile ? "sm" : "default"}
            onClick={() => {
              setEditPerfilId(null);
              setIsFormOpen(true);
            }}
          >
            <Plus className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
            {isMobile ? t("common.new") : t("ordenes.perfiles.newProfile")}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("ordenes.perfiles.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filters.is_active} onValueChange={(v) => setFilter("is_active", v)}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("ordenes.allStatus")}</SelectItem>
                  <SelectItem value="true">{t("common.active")}</SelectItem>
                  <SelectItem value="false">{t("common.inactive")}</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant="secondary" size="icon" onClick={clearFilters} className="shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardDescription>{t("ordenes.showingResults", { count: totalCount })}</CardDescription>
            {!isMobile && (
              <DataTableViewOptions
                table={table}
                columnLabels={{
                  codigo: t("ordenes.perfiles.columns.code"),
                  nombre: t("ordenes.perfiles.columns.name"),
                  precio: t("ordenes.perfiles.columns.price"),
                  is_active: t("ordenes.perfiles.columns.status"),
                }}
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveTable
            table={table}
            columns={columns}
            isLoading={isLoading}
            emptyMessage={t("ordenes.perfiles.noPerfiles")}
            hideOnTablet={["precio"]}
            renderMobileCard={(perfil) => (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm">{perfil.codigo}</span>
                  <Badge variant={perfil.is_active ? "default" : "secondary"}>
                    {perfil.is_active ? t("common.active") : t("common.inactive")}
                  </Badge>
                </div>
                <p className="font-medium">{perfil.nombre}</p>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{t("ordenes.perfiles.numExams", { count: perfil.num_examenes })}</span>
                  <span>L {Number(perfil.precio).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-end mt-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(perfil)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        {t("common.edit")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleToggleStatus(perfil)}>
                        {perfil.is_active ? (
                          <>
                            <XCircle className="mr-2 h-4 w-4" />
                            {t("common.deactivate")}
                          </>
                        ) : (
                          <>
                            <CheckCircle className="mr-2 h-4 w-4" />
                            {t("common.activate")}
                          </>
                        )}
                      </DropdownMenuItem>
                      {!perfil.is_active && (
                        <DropdownMenuItem
                          onClick={() => handleDelete(perfil)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t("common.delete")}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            )}
          />
          <DataTablePagination table={table} totalRows={totalCount} />
        </CardContent>
      </Card>

      <PerfilFormDialog
        open={isFormOpen}
        onOpenChange={(open) => {
          setIsFormOpen(open);
          if (!open) setEditPerfilId(null);
        }}
        perfilId={editPerfilId}
        onSuccess={handleFormSuccess}
      />

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={
          confirmAction === "activate"
            ? t("ordenes.perfiles.confirmActivate")
            : confirmAction === "delete"
              ? t("ordenes.perfiles.confirmDelete")
              : t("ordenes.perfiles.confirmDeactivate")
        }
        description={
          confirmAction === "activate"
            ? t("ordenes.perfiles.confirmActivateDesc", { name: selectedPerfil?.nombre })
            : confirmAction === "delete"
              ? t("ordenes.perfiles.confirmDeleteDesc", { name: selectedPerfil?.nombre })
              : t("ordenes.perfiles.confirmDeactivateDesc", { name: selectedPerfil?.nombre })
        }
        onConfirm={handleConfirmAction}
        variant={
          confirmAction === "deactivate" || confirmAction === "delete" ? "destructive" : "default"
        }
      />
    </div>
  );
}
